import type { FormatLocale } from '#layers/base/types/locale';
import { CalendarDate, CalendarDateTime, Time } from '@internationalized/date';
import { Temporal } from 'temporal-polyfill';

type DateFormatStyle = NonNullable<Intl.DateTimeFormatOptions['dateStyle']>;
type TimeFormatStyle = NonNullable<Intl.DateTimeFormatOptions['timeStyle']>;

interface FormatInstantWithTimeZoneOptions {
	timeZone?: string;
	locale: FormatLocale;
}

export type NativeDateInput = string | globalThis.Date | Temporal.Instant | Temporal.ZonedDateTime;

const plainDateStyleFormatOptions = {
	short: { month: 'numeric', day: 'numeric', year: '2-digit' },
	medium: { month: 'short', day: 'numeric', year: 'numeric' },
	long: { month: 'long', day: 'numeric', year: 'numeric' },
	full: { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' },
} satisfies Record<DateFormatStyle, Intl.DateTimeFormatOptions>;

const plainTimeStyleFormatOptions = {
	short: { hour: 'numeric', minute: '2-digit' },
	medium: { hour: 'numeric', minute: '2-digit', second: '2-digit' },
	long: { hour: 'numeric', minute: '2-digit', second: '2-digit' },
	full: { hour: 'numeric', minute: '2-digit', second: '2-digit' },
} satisfies Record<TimeFormatStyle, Intl.DateTimeFormatOptions>;

/**
 * Utilities for converting between Temporal API and @internationalized/date formats.
 * Temporal is used for date/time handling, while @internationalized/date types are required by Nuxt UI components.
 */
function createConverter<TCalendar, TTemporal>(
	toCalendar: (temporal: TTemporal) => TCalendar,
	toTemporal: (calendar: TCalendar) => TTemporal,
) {
	return {
		toCalendar: (value?: TTemporal | null) => (value ? toCalendar(value) : null),
		toTemporal: (value?: TCalendar | null) => (value ? toTemporal(value) : null),
	};
}

export const calendarDateConverter = createConverter<CalendarDate, Temporal.PlainDate>(
	({ year, month, day }) => new CalendarDate(year, month, day),
	({ year, month, day }) => new Temporal.PlainDate(year, month, day),
);

const generalPlainDateFormatOptions: Intl.DateTimeFormatOptions = { dateStyle: 'medium' };
const generalPlainTimeFormatOptions: Intl.DateTimeFormatOptions = { timeStyle: 'short' };
const generalPlainDateTimeFormatOptions: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' };
const preciseInstantFormatOptions: Intl.DateTimeFormatOptions = {
	weekday: 'short',
	month: 'short',
	day: 'numeric',
	year: 'numeric',
	hour: 'numeric',
	minute: '2-digit',
	second: '2-digit',
};

/** Locale-aware short date (e.g. en-US uses slashes). */
export const shortPlainDateFormatOptions: Intl.DateTimeFormatOptions = { dateStyle: 'short' };

export function formatPlainTime(
	time: Temporal.PlainTime | null | undefined,
	locale: FormatLocale,
	options?: Intl.DateTimeFormatOptions,
): string | undefined {
	if (time == null) return undefined;
	return time.toLocaleString(locale, options ?? generalPlainTimeFormatOptions);
}

export function getPlainDateTimeFormatOptions(options: {
	dateStyle?: DateFormatStyle;
	timeStyle?: TimeFormatStyle;
} = {}): Intl.DateTimeFormatOptions {
	return {
		...plainDateStyleFormatOptions[options.dateStyle ?? 'medium'],
		...plainTimeStyleFormatOptions[options.timeStyle ?? 'short'],
	};
}

export function formatPlainDateTime(
	dateTime: Temporal.PlainDateTime | null | undefined,
	locale: FormatLocale,
	options?: Intl.DateTimeFormatOptions,
): string | undefined {
	if (dateTime == null) return undefined;
	return dateTime.toLocaleString(locale, options ?? generalPlainDateTimeFormatOptions);
}

export function formatPlainDate(
	date: Temporal.PlainDate | null | undefined,
	locale: FormatLocale,
	options?: Intl.DateTimeFormatOptions,
): string | undefined {
	if (date == null) return undefined;
	return date.toLocaleString(locale, options ?? generalPlainDateFormatOptions);
}

export const calendarDateTimeConverter = createConverter<CalendarDateTime, Temporal.PlainDateTime>(
	({ year, month, day, hour, minute, second, millisecond }) =>
		new CalendarDateTime(year, month, day, hour, minute, second, millisecond),
	({ year, month, day, hour, minute, second, millisecond }) =>
		new Temporal.PlainDateTime(year, month, day, hour, minute, second, millisecond),
);

export const timeConverter = createConverter<Time, Temporal.PlainTime>(
	({ hour, minute, second, millisecond }) => new Time(hour, minute, second, millisecond),
	({ hour, minute, second, millisecond }) => new Temporal.PlainTime(hour, minute, second, millisecond),
);

/**
 * Whether a value is an epoch timestamp payload. Integer timestamp columns (int64/uint64) arrive
 * as strings on the wire to preserve precision, so both numbers and strings are accepted.
 */
export function isEpochTimestampValue(value: unknown): value is number | string {
	return typeof value === 'number'
		? Number.isSafeInteger(value)
		: typeof value === 'string' && /^-?\d+$/.test(value);
}

export const unixTimestampConverter = {
	toTemporal: (timestamp: number | string | null): Temporal.Instant | null => {
		if (timestamp == null || !isEpochTimestampValue(timestamp)) return null;
		const epochMilliseconds = typeof timestamp === 'string' ? Number(timestamp) : timestamp;

		if (!Number.isSafeInteger(epochMilliseconds)) return null;

		try {
			return Temporal.Instant.fromEpochMilliseconds(epochMilliseconds);
		}
		catch {
			return null;
		}
	},
	fromTemporal: (instant: Temporal.Instant | null): number | null => {
		if (instant == null) return null;
		return Number(instant.epochMilliseconds);
	},
	toPlainDateTime: (timestamp: number | string | null, timeZone = 'UTC'): Temporal.PlainDateTime | null => {
		const instant = unixTimestampConverter.toTemporal(timestamp);
		if (!instant) return null;
		return instant.toZonedDateTimeISO(timeZone).toPlainDateTime();
	},
	fromPlainDateTime: (dateTime: Temporal.PlainDateTime | null, timeZone = 'UTC'): number | null => {
		if (!dateTime) return null;
		const instant = dateTime.toZonedDateTime(timeZone).toInstant();
		return unixTimestampConverter.fromTemporal(instant);
	},
};

export function formatTechnicalDateTime(dateTime: Temporal.PlainDateTime): string {
	return dateTime
		.toString({ smallestUnit: 'second' })
		.replace('T', ' ');
}

export function toNativeDate(date: NativeDateInput): globalThis.Date {
	if (typeof date === 'string' || date instanceof Date) {
		return new globalThis.Date(date);
	}

	if ('toInstant' in date) {
		return new globalThis.Date(Number(date.toInstant().epochMilliseconds));
	}

	return new globalThis.Date(Number(date.epochMilliseconds));
}

export function isPastDateTime(dateTime: NativeDateInput, now = Date.now()): boolean {
	return toNativeDate(dateTime).getTime() <= now;
}

/** Formats an instant in the selected timezone with the standard localized date/time presentation. */
export function formatInstantWithTimeZone(
	instant: NativeDateInput,
	options: FormatInstantWithTimeZoneOptions,
): string {
	const timeZone = options.timeZone ?? Temporal.Now.timeZoneId();
	const dateTime = toInstant(instant).toZonedDateTimeISO(timeZone).toPlainDateTime();

	return formatPlainDateTime(dateTime, options.locale) ?? '';
}

export function formatPreciseInstantWithTimeZone(
	instant: NativeDateInput,
	options: FormatInstantWithTimeZoneOptions,
): string {
	const timeZone = options.timeZone ?? Temporal.Now.timeZoneId();

	return new Intl.DateTimeFormat(options.locale, {
		...preciseInstantFormatOptions,
		timeZone,
	}).format(toNativeDate(toInstant(instant)));
}

export function formatUnixTimestampSeconds(instant: NativeDateInput): string {
	return Math.floor(Number(toInstant(instant).epochMilliseconds) / 1000).toString();
}

export function toInstant(instant: NativeDateInput): Temporal.Instant {
	if (typeof instant === 'string') {
		return Temporal.Instant.from(instant);
	}

	if (instant instanceof Date) {
		return Temporal.Instant.fromEpochMilliseconds(instant.getTime());
	}

	if ('toInstant' in instant) {
		return instant.toInstant();
	}

	return instant;
}

export const nowDate = () => Temporal.Now.plainDateISO();

export const nowDateTime = () => Temporal.Now.plainDateTimeISO();

export const nowTime = () => Temporal.Now.plainTimeISO();

// Month bounds for `date`'s month as PlainDate; current month if missing/invalid.
export function getMonthRangeForDate(date?: string): { start: Temporal.PlainDate; end: Temporal.PlainDate } {
	let base: Temporal.PlainDate;

	try {
		base = date ? Temporal.PlainDate.from(date.slice(0, 10)) : nowDate();
	}
	catch {
		base = nowDate();
	}

	return {
		start: base.with({ day: 1 }),
		end: base.with({ day: base.daysInMonth }),
	};
}
